import React from 'react';
import { CheckCircle2Icon, MoonIcon, PauseCircleIcon } from 'lucide-react';
import type { LeadSource, LoanOfficer } from '../data/leadDistribution';
import type { DailyTimeBlock } from '../utils/timeBlocks';
import { LastAssigned } from './LastAssigned';
import { LeadSourcesCell } from './LeadSourcesCell';
import { ReadyToggle } from './ReadyToggle';
import { TimeBlocksCell } from './TimeBlocksCell';
interface LoanOfficerRowProps {
  officer: LoanOfficer;
  sources: LeadSource[];
  isNextUp: boolean;
  now: Date;
  onTimeBlocksChange: (blocks: DailyTimeBlock[]) => void;
  onToggleReady: () => void;
  onAddSource: (sourceId: LeadSource['id']) => void;
  onAdjustCap: (sourceId: LeadSource['id'], delta: number) => void;
  onOpenLicenses: () => void;
}
function initials(name: string) {
  return name.split(' ').map(part => part[0]).slice(0, 2).join('');
}
export function LoanOfficerRow({
  officer,
  sources,
  isNextUp,
  now,
  onTimeBlocksChange,
  onToggleReady,
  onAddSource,
  onAdjustCap,
  onOpenLicenses
}: LoanOfficerRowProps) {
  const rowBackground = officer.ready ? 'bg-white' : 'bg-slate-50/70';
  return <tr className={`group border-b border-slate-100 transition-colors last:border-b-0 hover:bg-rcm-50/50 ${rowBackground}`} data-id="element-81" data-mp="27bqjwc2zq0vu">
      <td className={`sticky left-0 z-10 min-w-[220px] px-4 py-3.5 transition-colors group-hover:bg-rcm-50/50 ${rowBackground}`} data-id="element-82" data-mp="1td0k2oosdn3s">
        <div className="flex items-center gap-3" data-id="element-83" data-mp="1ycwhu66w4iaw">
          <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${officer.ready ? 'bg-rcm-500 text-white' : 'bg-slate-200 text-slate-500'}`} data-id="element-84" data-mp="2m1fnsf2gwizt">
            {initials(officer.name)}
          </div>
          <div className="min-w-0" data-id="element-85" data-mp="2c8gy0tv8imem">
            <div className="flex items-center gap-1.5" data-id="element-86" data-mp="2h94mb7s2wp55">
              <span className="truncate text-sm font-bold text-slate-800" data-id="element-87" data-mp="2h7jtpnpbrg9x">{officer.name}</span>
              {isNextUp && <span className="rounded-full bg-rcm-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-rcm-700" data-id="element-88" data-mp="34xe47ym68sr0">Next up</span>}
            </div>
            <span className="truncate text-xs text-slate-400" data-id="element-89" data-mp="3jkmmfjdwdych">{officer.email}</span>
          </div>
        </div>
      </td>

      <TimeBlocksCell blocks={officer.timeBlocks} now={now} officerName={officer.name} onChange={onTimeBlocksChange} data-id="element-90" data-mp="3enmc2wa0m9fk" />

      <td className="min-w-[176px] px-4 py-3.5" data-id="element-91" data-mp="39pktecgi4c4m">
        <div className="flex items-center gap-2.5" data-id="element-92" data-mp="34nn8zewd7ba3">
          <ReadyToggle ready={officer.ready} disabled={false} onToggle={onToggleReady} label={`Set ${officer.name} to ${officer.ready ? 'not ready' : 'ready'}`} data-id="element-93" data-mp="30n37y08o7nio" />
          {officer.manualReady === true ? <span className="inline-flex items-center gap-1 text-xs font-bold text-rcm-600" data-id="element-94" data-mp="2qxjpuwss7xov"><CheckCircle2Icon className="h-3.5 w-3.5" data-id="element-95" data-mp="2lzi6sbvcvyde" /> Admin enabled</span> : officer.manualReady === false ? <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700" data-id="element-96" data-mp="2gyax279nkp43"><PauseCircleIcon className="h-3.5 w-3.5" data-id="element-97" data-mp="2cysh7pwov484" /> Admin paused</span> : officer.ready ? <span className="inline-flex items-center gap-1 text-xs font-bold text-rcm-600" data-id="element-98" data-mp="sewz7vhuj92r"><CheckCircle2Icon className="h-3.5 w-3.5" data-id="element-99" data-mp="wg0ikrz3rn8q" /> Scheduled on</span> : <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-400" data-id="element-100" data-mp="11h6awooup8hq"><MoonIcon className="h-3.5 w-3.5" data-id="element-101" data-mp="16fraqn06xq5n" /> Outside schedule</span>}
        </div>
      </td>

      <td className="min-w-[150px] px-4 py-3.5" data-id="element-102" data-mp="8s8tensgrfq1">
        <button type="button" onClick={onOpenLicenses} aria-label={`View all state licenses for ${officer.name}`} className="-m-1 flex flex-wrap gap-1 rounded-md p-1 text-left transition-colors hover:bg-rcm-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-rcm-500" data-id="element-103" data-mp="8pe9cxdd3wx5">
          {officer.licensedStates.slice(0, 4).map(state => <span key={state} className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-600 group-hover:bg-white" data-id="element-104" data-mp="3m5ub6n9bxpny">{state}</span>)}
          {officer.licensedStates.length > 4 && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-400" data-id="element-105" data-mp="1wq1jh5dqbmes">+{officer.licensedStates.length - 4}</span>}
        </button>
      </td>

      <td className="min-w-[170px] px-4 py-3.5" data-id="element-106" data-mp="26fba0gsxcx0s"><LastAssigned timestamp={officer.lastAssignedAt} data-id="element-107" data-mp="26gco6u58lzmq" /></td>

      <LeadSourcesCell capacities={officer.capacities} sources={sources} officerId={officer.id} onAddSource={onAddSource} onAdjustCap={onAdjustCap} data-id="element-108" data-mp="21o343uqsztsd" />
    </tr>;
}